import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Printer, Download, ArrowLeft } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const Invoice = () => {
  const { orderNumber } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrder();
  }, [orderNumber]);

  const fetchOrder = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/orders/track/${orderNumber}`);
      if (!response.ok) throw new Error('Order not found');
      const data = await response.json();
      setOrder(data);
    } catch (error) {
      console.error('Error fetching order:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const generateInvoiceNumber = (orderNumber) => {
    // Convert order number to invoice number (e.g., ORD-123 -> INV-2024-123)
    const year = new Date().getFullYear();
    const invoiceNum = orderNumber.replace('ORD-', '');
    return `INV-${year}-${invoiceNum}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Loading invoice...</p>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Invoice Not Found</h2>
          <Button onClick={() => navigate('/')}>Go to Homepage</Button>
        </div>
      </div>
    );
  }

  const invoiceNumber = generateInvoiceNumber(order.order_number);
  const invoiceDate = new Date(order.created_at).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  const taxRate = 0.05; // 5% VAT (adjust as needed)
  const taxAmount = order.subtotal * taxRate;
  const totalWithTax = order.total + taxAmount;

  return (
    <>
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          #invoice-content, #invoice-content * {
            visibility: visible;
          }
          #invoice-content {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
          .no-print {
            display: none !important;
          }
          .print-break {
            page-break-after: always;
          }
        }
        
        @page {
          size: A4;
          margin: 1cm;
        }
      `}</style>

      <div className="min-h-screen bg-gray-100 py-8">
        {/* Action Buttons - Hidden on print */}
        <div className="max-w-4xl mx-auto px-4 mb-4 no-print flex gap-3">
          <Button
            variant="outline"
            onClick={() => navigate(-1)}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
          <Button
            onClick={handlePrint}
            className="flex items-center gap-2 bg-orange-600 hover:bg-orange-700"
          >
            <Printer className="w-4 h-4" />
            Print Invoice
          </Button>
        </div>

        {/* Invoice Content */}
        <div id="invoice-content" className="max-w-4xl mx-auto bg-white shadow-lg">
          <div className="p-8">
            {/* Header */}
            <div className="flex justify-between items-start mb-8 border-b-2 border-orange-600 pb-6">
              <div>
                <div className="bg-gradient-to-r from-orange-600 to-red-600 text-white px-6 py-3 rounded-lg inline-block mb-4">
                  <h1 className="text-3xl font-bold">MEGA parts OMAN</h1>
                </div>
                <p className="text-gray-600">Premium Auto Parts Supplier</p>
                <p className="text-sm text-gray-500">Muscat, Sultanate of Oman</p>
                <p className="text-sm text-gray-500">Tel: +968 1234 5678</p>
                <p className="text-sm text-gray-500">Email: info@megapartsoman.com</p>
                <p className="text-sm text-gray-500">TRN: 1234567890123</p>
              </div>
              <div className="text-right">
                <h2 className="text-3xl font-bold text-gray-800 mb-2">INVOICE</h2>
                <div className="bg-gray-100 p-4 rounded">
                  <p className="text-sm text-gray-600">Invoice Number</p>
                  <p className="text-xl font-bold text-orange-600">{invoiceNumber}</p>
                  <p className="text-sm text-gray-600 mt-2">Invoice Date</p>
                  <p className="font-semibold">{invoiceDate}</p>
                </div>
              </div>
            </div>

            {/* Bill To & Order Info */}
            <div className="grid grid-cols-2 gap-8 mb-8">
              <div>
                <h3 className="text-lg font-bold text-gray-800 mb-3 border-l-4 border-orange-600 pl-3">
                  BILL TO
                </h3>
                <div className="bg-gray-50 p-4 rounded">
                  <p className="font-semibold text-gray-800">
                    {order.customer_info.first_name} {order.customer_info.last_name}
                  </p>
                  <p className="text-sm text-gray-600">{order.customer_info.email}</p>
                  <p className="text-sm text-gray-600">{order.customer_info.phone}</p>
                </div>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-800 mb-3 border-l-4 border-orange-600 pl-3">
                  SHIP TO
                </h3>
                <div className="bg-gray-50 p-4 rounded">
                  <p className="font-semibold text-gray-800">
                    {order.customer_info.first_name} {order.customer_info.last_name}
                  </p>
                  <p className="text-sm text-gray-600">{order.shipping_address?.address_line1}</p>
                  {order.shipping_address?.address_line2 && (
                    <p className="text-sm text-gray-600">{order.shipping_address.address_line2}</p>
                  )}
                  <p className="text-sm text-gray-600">
                    {order.shipping_address?.city}, {order.shipping_address?.postal_code}
                  </p>
                  <p className="text-sm text-gray-600">{order.shipping_address?.country}</p>
                </div>
              </div>
            </div>

            {/* Order Details */}
            <div className="mb-6">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="bg-blue-50 p-3 rounded">
                  <span className="text-gray-600">Order Number:</span>
                  <span className="font-semibold ml-2">{order.order_number}</span>
                </div>
                <div className="bg-blue-50 p-3 rounded">
                  <span className="text-gray-600">Order Status:</span>
                  <span className="font-semibold ml-2 uppercase">{order.status}</span>
                </div>
              </div>
            </div>

            {/* Items Table */}
            <div className="mb-8">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-800 text-white">
                    <th className="text-left p-3 border">#</th>
                    <th className="text-left p-3 border">Product Description</th>
                    <th className="text-center p-3 border">Quantity</th>
                    <th className="text-right p-3 border">Unit Price</th>
                    <th className="text-right p-3 border">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {order.items.map((item, index) => (
                    <tr key={index} className="border-b">
                      <td className="p-3 border">{index + 1}</td>
                      <td className="p-3 border">
                        <p className="font-semibold">{item.product_name}</p>
                        <p className="text-sm text-gray-600">{item.brand}</p>
                      </td>
                      <td className="p-3 border text-center">{item.quantity}</td>
                      <td className="p-3 border text-right">OMR {item.price.toFixed(2)}</td>
                      <td className="p-3 border text-right font-semibold">
                        OMR {(item.price * item.quantity).toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Totals */}
            <div className="flex justify-end mb-8">
              <div className="w-80">
                <div className="bg-gray-50 p-4 rounded">
                  <div className="flex justify-between mb-2 pb-2 border-b">
                    <span className="text-gray-600">Subtotal:</span>
                    <span className="font-semibold">OMR {order.subtotal.toFixed(2)}</span>
                  </div>
                  {order.discount_amount > 0 && (
                    <div className="flex justify-between mb-2 pb-2 border-b text-green-600">
                      <span>Discount ({order.coupon_code}):</span>
                      <span className="font-semibold">-OMR {order.discount_amount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between mb-2 pb-2 border-b">
                    <span className="text-gray-600">Shipping:</span>
                    <span className="font-semibold">
                      {order.shipping_cost === 0 ? 'FREE' : `OMR ${order.shipping_cost.toFixed(2)}`}
                    </span>
                  </div>
                  <div className="flex justify-between mb-2 pb-2 border-b">
                    <span className="text-gray-600">VAT (5%):</span>
                    <span className="font-semibold">OMR {taxAmount.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-lg pt-2 border-t-2 border-gray-800">
                    <span className="font-bold">TOTAL:</span>
                    <span className="font-bold text-orange-600">OMR {totalWithTax.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Payment Info */}
            <div className="mb-8 bg-blue-50 p-4 rounded">
              <h3 className="font-bold text-gray-800 mb-2">Payment Information</h3>
              <p className="text-sm text-gray-600">
                Payment Method: <span className="font-semibold">{order.payment_status === 'paid' ? 'Card Payment' : 'Cash on Delivery'}</span>
              </p>
              <p className="text-sm text-gray-600">
                Payment Status: <span className="font-semibold uppercase">{order.payment_status}</span>
              </p>
            </div>

            {/* Terms & Conditions */}
            <div className="border-t-2 border-gray-300 pt-6">
              <h3 className="font-bold text-gray-800 mb-3">Terms & Conditions</h3>
              <div className="text-xs text-gray-600 space-y-1">
                <p>• All prices are in Omani Rial (OMR) and include VAT where applicable.</p>
                <p>• Goods remain the property of MEGA parts OMAN until payment is received in full.</p>
                <p>• Returns are accepted within 14 days with original packaging and receipt.</p>
                <p>• Installation services are available at additional cost.</p>
                <p>• Warranty terms apply as per manufacturer specifications.</p>
                <p>• For any queries, please contact our customer service at support@megapartsoman.com</p>
              </div>
            </div>

            {/* Footer */}
            <div className="mt-8 pt-6 border-t text-center">
              <p className="text-sm text-gray-600">Thank you for your business!</p>
              <p className="text-xs text-gray-500 mt-2">
                This is a computer-generated invoice and does not require a signature.
              </p>
              <p className="text-xs text-gray-500">
                MEGA parts OMAN | Muscat, Oman | www.megapartsoman.com
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Invoice;
